import { meanEmbedding } from './meanEmbedding';
import type { PatchTensor } from './PatchTensor';

export type CosineSimilarityHeatmapArgs = {
  tensor: PatchTensor;
  positives: ReadonlyArray<{ values: ReadonlyArray<number> }>;
  negatives: ReadonlyArray<{ values: ReadonlyArray<number> }>;
  // Default 0.8 matches the spec's hard-coded score formula:
  //   score(p) = dot(p, meanPos) − 0.8·dot(p, meanNeg).
  negativeWeight?: number;
};

export const DEFAULT_NEGATIVE_WEIGHT = 0.8;

export function cosineSimilarityHeatmap(
  args: CosineSimilarityHeatmapArgs,
): Float32Array {
  const { tensor, positives, negatives } = args;
  const negativeWeight = args.negativeWeight ?? DEFAULT_NEGATIVE_WEIGHT;

  if (positives.length === 0) {
    throw new Error(
      'cosineSimilarityHeatmap: at least one positive reference is required',
    );
  }

  const meanPos = meanEmbedding(positives);
  const hasNegatives = negatives.length > 0;
  const meanNeg = hasNegatives ? meanEmbedding(negatives) : null;

  if (meanPos.length !== tensor.dim) {
    throw new Error(
      `cosineSimilarityHeatmap: positive dim ${meanPos.length} != tensor dim ${tensor.dim}`,
    );
  }

  if (meanNeg && meanNeg.length !== tensor.dim) {
    throw new Error(
      `cosineSimilarityHeatmap: negative dim ${meanNeg.length} != tensor dim ${tensor.dim}`,
    );
  }

  const { gridH, gridW, dim } = tensor;
  const out = new Float32Array(gridH * gridW);

  // Use a single scratch buffer so the inner loop doesn't allocate per patch.
  const scratch = new Float32Array(dim);

  for (let row = 0; row < gridH; row += 1) {
    for (let col = 0; col < gridW; col += 1) {
      tensor.fillPatch(row, col, scratch);
      let dotPos = 0;

      for (let i = 0; i < dim; i += 1) {
        dotPos += scratch[i] * meanPos[i];
      }

      let score = dotPos;

      if (meanNeg) {
        let dotNeg = 0;

        for (let i = 0; i < dim; i += 1) {
          dotNeg += scratch[i] * meanNeg[i];
        }

        score -= negativeWeight * dotNeg;
      }

      out[row * gridW + col] = score;
    }
  }

  return out;
}
